import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PlantApi from "../../services/PlantApi";
import CompanyAPI from "../../services/CompanyApi";
import { useToast } from "../../context/ToastContext";
import IApiPlantsResponse from "../../interfaces/IApiPlantsResponse";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const EditFormPlant: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companies, setCompanies] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const companiesResponse = await CompanyAPI.GetAllCompanies();
      const options = (companiesResponse.data || []).map((c: any) => ({
        label: c.name,
        value: c.id,
      }));
      setCompanies(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));

      const plantsResponse = await PlantApi.GetAllPlants();
      const plant = (plantsResponse.data || []).find((p: IApiPlantsResponse) => p.id === id);

      if (plant) {
        setName(plant.name);
        setCompanyId(plant.companyId);
      } else {
        displayToast("error", translateForm("error"), translateGeneral("noRecordsFound"), false, 3000);
        navigate("/plants");
      }
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (): Promise<void> => {
    if (!name || !companyId || !id) {
      displayToast("error", translateForm("error"), translateMessages("fillAllFields"), false, 3000);
      return;
    }

    setIsLoading(true);
    try {
      await PlantApi.UpdatePlant(id, companyId, name);
      displayToast("success", translateMessages("editSuccess"), "", false, 3000);
      navigate("/plants");
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard
      title={translateMessages("editPlant")}
      icon="edit"
      onSubmit={handleUpdate}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translateGeneral("save")}
      cancelButtonLabel={translateGeneral("cancel")}
      disabled={isLoading}
      numColumns={6}
    >
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("name")}
          required
          value={name}
          onChange={(val: any) => setName(val)}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("company")}
          required
          value={companyId}
          options={companies}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => setCompanyId(val)}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/plants")}
      />
    </FormCard>
  );
};

export default EditFormPlant;
